import { ChevronsUpDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { languageToCountryFlag } from '../lib/utils';
import { AppConfig } from '../utils/AppConfig';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Preferences } from '@capacitor/preferences';
import i18n from '@/lib/i18n';
import { Button } from './ui/button';

type LocaleKey = keyof typeof AppConfig.languageLabels;

export const LocaleSwitcher = ({ flatDisplay = false }: { flatDisplay?: boolean }) => {
  const [locale, setLocale] = useState<LocaleKey>('en');
  const [open, setOpen] = useState(false);
  const locales = AppConfig.languageLabels;

  useEffect(() => {
    loadCurrentLocale();
  }, []);

  const loadCurrentLocale = async () => {
    const { value } = await Preferences.get({ key: 'locale' });
    if (value) setLocale(value as LocaleKey);
  };

  const handleValueChange = async (newLocale: LocaleKey) => {
    await Preferences.set({ key: 'locale', value: newLocale });
    setLocale(newLocale);
    i18n.changeLanguage(newLocale);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={`w-fit ${flatDisplay ? 'bg-transparent' : 'w-full justify-start border'}`}
        >
          <div className="flex items-center text-base">
            <img
              alt={locale}
              height={16}
              width={24}
              src={`/assets/images/flags/${languageToCountryFlag(locale)}.svg`}
              className="mr-2 rounded shadow-sm"
            />
            {locales[locale]}
          </div>
          <ChevronsUpDown className="ml-auto size-5 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(locales).map(([key, value]) => (
            <Button
              key={key}
              variant="ghost"
              className="w-full justify-start"
              onClick={() => handleValueChange(key as LocaleKey)}
            >
              <div className="flex items-center text-base">
                <img
                  alt={key}
                  height={16}
                  width={24}
                  src={`/assets/images/flags/${languageToCountryFlag(key)}.svg`}
                  className="mr-2 rounded shadow-sm"
                />
                {value as string}
              </div>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
